import { api } from '@carrent/api'
import { offline } from '@carrent/platform'

/**
 * Приёмка и сдача через офлайн-очередь.
 *
 * ── Зачем ─────────────────────────────────────────────────────────
 *
 * Водитель принимает машину на парковке, в подземном гараже, на базе
 * за городом. Связь там появляется рывками. До этого визард отправлял
 * акт напрямую: обрыв на девяноста процентах означал, что шесть фото,
 * отметки повреждений и полчаса работы исчезали.
 *
 * Теперь порядок другой: сначала всё ложится на устройство, потом
 * ставится задача в очередь, и только потом очередь пытается
 * отправить. Обрыв стоит одной попытки, а не всей приёмки.
 *
 * ── Почему фото не в payload задачи ───────────────────────────────
 *
 * Blob в payload задачи означал бы, что снимок лежит в IndexedDB
 * дважды: в хранилище фото и внутри задачи. При шести снимках это
 * лишние мегабайты на устройстве, где место и так кончается.
 * Поэтому в задаче лежат только идентификаторы, а сборка multipart
 * происходит в момент отправки.
 *
 * ── Идемпотентность ───────────────────────────────────────────────
 *
 * Ключ генерируется очередью один раз и не меняется при повторах —
 * сервер по нему узнаёт дубль. Отметки повреждений защищены вторым
 * рубежом: client_mark_id уникален в паре с поездкой, поэтому даже
 * задача, повторённая после частичного успеха, не создаст вторую
 * точку.
 */

const { outbox, photos, worker } = offline

/** Категория снимка повреждения. photoId принимает произвольную строку,
 *  поэтому отдельного хранилища для точек не нужно. */
export const damageCategory = (clientMarkId) => `damage:${clientMarkId}`
export const extraCategory = (index) => `extra:${index}`

/**
 * Сохранить снимки акта на устройство.
 *
 * Делается до постановки задачи: если приложение закроется между
 * этими шагами, снимки останутся и очистятся политикой квот,
 * а вот задача без снимков ушла бы на сервер неполной.
 */
async function persistPhotos({ draftId, phase, photoMap, extras, marks }) {
  for (const [category, file] of Object.entries(photoMap)) {
    if (file) await photos.savePhoto({ draftId, phase, category, file })
  }
  for (const [i, extra] of extras.entries()) {
    await photos.savePhoto({
      draftId, phase, category: extraCategory(i), file: extra.file,
    })
  }
  for (const mark of marks) {
    if (mark.file) {
      await photos.savePhoto({
        draftId, phase, category: damageCategory(mark.client_mark_id), file: mark.file,
      })
    }
  }
}

/** Метаданные отметок: File сюда не попадает — он уже в хранилище. */
const markPayload = (marks) => marks.map((m) => ({
  client_mark_id: m.client_mark_id,
  status: m.status,
  zone_id: m.zone_id,
  x: m.x,
  y: m.y,
  damage_type: m.damage_type,
  severity: m.severity,
  car_damage_id: m.car_damage_id,
  client_occurred_at: m.client_occurred_at,
}))

export async function enqueueStartTrip({
  draftId, carId, startMileage, comment, photoMap, extras = [], marks = [], geo = null,
}) {
  await persistPhotos({ draftId, phase: 'start', photoMap, extras, marks })
  const task = await outbox.enqueue({
    type: outbox.TYPE_START_TRIP,
    draftId,
    // Один черновик — одна задача старта: повторное нажатие «Начать»
    // заменяет её, а не ставит вторую.
    dedupeKey: `start:${draftId}`,
    payload: {
      draftId,
      phase: 'start',
      car_id: String(carId),
      start_mileage: String(startMileage),
      comment: comment || '',
      marks: markPayload(marks),
      geo,
    },
  })
  return task
}

export async function enqueueFinishTrip({
  draftId, tripId, endMileage, hasDamage, comment, photoMap, extras = [], marks = [],
  geo = null,
}) {
  await persistPhotos({ draftId, phase: 'end', photoMap, extras, marks })
  const task = await outbox.enqueue({
    type: outbox.TYPE_FINISH_TRIP,
    draftId,
    dedupeKey: `finish:${tripId}`,
    payload: {
      draftId,
      phase: 'end',
      trip_id: tripId,
      end_mileage: String(endMileage),
      has_damage: hasDamage ? 'true' : 'false',
      comment: comment || '',
      marks: markPayload(marks),
      geo,
    },
  })
  return task
}

/** Потолок пачки — тот же, что принимает сервер. */
export const MAX_POINTS_PER_TASK = 100

/**
 * Поставить точки трека в очередь, доклеивая к уже ждущей пачке.
 *
 * ── Почему доклейка, а не задача на точку ─────────────────────────
 *
 * Точка раз в минуту при задаче на каждую дала бы шестьдесят запросов
 * в час на водителя. Пачка уезжает одним запросом, а на разрыве связи
 * копится дальше.
 *
 * ── Почему доклеиваем только к ждущей ─────────────────────────────
 *
 * К задаче, которая уже в полёте, дописывать нельзя: она может
 * завершиться раньше, чем доедет дописанное, и точки потеряются.
 * В этом случае заводится новая задача. Даже если точка в итоге
 * уедет дважды, вреда нет — сервер отсеет её по client_point_id,
 * и на этом построена вся идемпотентность доставки.
 */
export async function enqueueTripLocations({ tripId, points }) {
  if (!points?.length) return null

  const waiting = (await outbox.all()).find(
    (t) => t.type === outbox.TYPE_TRIP_LOCATIONS
      && t.payload?.trip_id === tripId
      && t.status === outbox.PENDING,
  )

  if (waiting && waiting.payload.points.length + points.length <= MAX_POINTS_PER_TASK) {
    return outbox.enqueue({
      type: outbox.TYPE_TRIP_LOCATIONS,
      dedupeKey: waiting.dedupeKey,
      payload: {
        trip_id: tripId,
        points: [...waiting.payload.points, ...points],
      },
    })
  }

  return outbox.enqueue({
    type: outbox.TYPE_TRIP_LOCATIONS,
    // Уникален на пачку, а не на поездку: иначе следующая пачка
    // заменила бы собой ту, что ещё не уехала. Одного времени мало —
    // две пачки в одну миллисекунду получили бы общий ключ, и вторая
    // затёрла бы первую вместе с её точками.
    dedupeKey: `locations:${tripId}:${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    payload: { trip_id: tripId, points: points.slice(0, MAX_POINTS_PER_TASK) },
  })
}

/**
 * Собрать multipart из хранилища.
 *
 * Категория снимка кодирует его роль: damage:<id> — фото точки,
 * extra:<n> — дополнительный кадр, всё остальное — обязательная
 * категория приёмки.
 */
async function buildFormData(payload) {
  const fd = new FormData()
  const stored = await photos.listPhotos(payload.draftId, payload.phase)

  for (const record of stored) {
    const { category, blob } = record
    const file = new File([blob], `${category.replace(/[:]/g, '-')}.jpg`,
      { type: record.type || 'image/jpeg' })

    if (category.startsWith('damage:')) {
      fd.append(`photo_damage_${category.slice('damage:'.length)}`, file)
    } else if (category.startsWith('extra:')) {
      fd.append(`photo_extra_${category.slice('extra:'.length)}`, file)
    } else {
      fd.append(`photo_${category}`, file)
    }
  }

  for (const key of ['car_id', 'start_mileage', 'end_mileage', 'has_damage']) {
    if (payload[key] !== undefined) fd.append(key, payload[key])
  }

  // Координата акта. Уезжает как есть, вместе с источником: сервер
  // отбросит её, если она непригодна, но приёмку не сорвёт.
  // Источник передаётся всегда — без него точка неотличима
  // от снятой по сети с километровой погрешностью.
  if (payload.geo?.source && payload.geo.source !== 'none') {
    fd.append('geo_lat', String(payload.geo.lat))
    fd.append('geo_lng', String(payload.geo.lng))
    fd.append('geo_source', payload.geo.source)
    if (payload.geo.accuracy != null) fd.append('geo_accuracy', String(payload.geo.accuracy))
    if (payload.geo.at) fd.append('geo_at', payload.geo.at)
  }
  if (payload.comment) fd.append('comment', payload.comment)
  if (payload.marks?.length) {
    fd.append('damage_marks', JSON.stringify(payload.marks))
  }

  return { fd, stored }
}

/**
 * Обработчики очереди.
 *
 * Бросают исключение при неудаче — очередь сама решает, повторять
 * задачу или пометить её окончательно неуспешной: 4xx означает
 * «данные не подойдут и в следующий раз», всё остальное — ретрай.
 */
export function tripHandlers({ onTripStarted, onTripFinished } = {}) {
  return {
    [outbox.TYPE_START_TRIP]: async (task) => {
      const { fd } = await buildFormData(task.payload)
      const { data } = await api.post('/trips/start', fd, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Idempotency-Key': task.idempotencyKey,
        },
      })
      // Снимки удаляем только после подтверждения сервером: до этого
      // они единственная копия работы водителя.
      await photos.deleteDraft(task.payload.draftId)
      onTripStarted?.(data?.data)
    },

    [outbox.TYPE_TRIP_LOCATIONS]: async (task) => {
      const { trip_id: tripId, points } = task.payload
      await api.post(`/trips/${tripId}/locations`, { points })
      // Ответ не разбираем: accepted и skipped равно означают,
      // что точки у сервера есть и задачу можно закрывать.
    },

    [outbox.TYPE_FINISH_TRIP]: async (task) => {
      const { fd } = await buildFormData(task.payload)
      const { data } = await api.post(`/trips/${task.payload.trip_id}/finish`, fd, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Idempotency-Key': task.idempotencyKey,
        },
      })
      await photos.deleteDraft(task.payload.draftId)
      onTripFinished?.(data?.data)
    },
  }
}

/** Запустить очередь с обработчиками поездок. */
export function startTripQueue(callbacks) {
  return worker.start(tripHandlers(callbacks))
}

/**
 * Отправить прямо сейчас и дождаться результата.
 *
 * Нужен визарду: при живой сети водитель должен увидеть обычный
 * переход в поездку, а не «отправится позже». Если разгрузка
 * не удалась, задача остаётся в очереди — работа не потеряна.
 */
export async function flushNow() {
  return worker.flush()
}
